import { createSlice, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { checkoutInfoClient, commonClient } from "../../core/api";
import {
  startLoading,
  endLoading,
} from "../../components/basic/LoadingProgress/loadingProgressSlice";

export interface CheckoutInfoState {
  basic: {
    registrationDetails: registrationDetail;
    pricingType: string;
    financePrice: number;
  } | null;
  financeOptions: {
    [key: string]: {
      title: string;
      group: string;
      name: string;
      term: string;
      interest: number;
      productId: string;
      productGuid: string;
      maxLoan: number;
      minLoan: number;
      monthlyRate: number;
      calculationFactor: number;
      documentFee: number;
      documentFeePercentage: number;
      documentFeeMinimum: number;
      documentFeeMaximum: number;
      documentFeeCollectionMonth: number;
      serviceFee: number;
      settlementFee: number;
      deferredPeriod: number;
      optionPeriod: number;
      altTag: string;
      description: string;
    };
  };
  customisePlateOptions?: {
    [key: string]: {
      [key: string]: {
        name: string;
        price: number | string;
      };
    };
  };
  financePrice: number;
  maximumDeposit: number;
  creditInformation: {
    apr: number;
    loan_cost: string;
    loan_repayment: string;
    payment_regular: string;
    payment_regular_detail: string;
    annual_rate: string;
  };
  fetchingDekoOptionStatus: boolean;
  fetchingBasicInfoStatus: boolean;
  submitFinanceStatus: boolean;
  deliveryPrices?: {
    special: string;
    recorded: string;
    standard: string;
  };
  formValues: {
    [key: string]: string;
  };
  faqs?: [
    {
      question: string;
      answer: string;
    }
  ];
  salesFormMessage?: string;
  sourcecode: string;
}

export type registrationDetail = {
  registration: string;
  originalPrice: string;
  price: string;
  transfer_fee: string;
  vat: string;
  web_vat: string;
  price_plus_vat: number;
  sourcecode: string;
  display_date1: string;
  display_price: string;
  display_vat: string;
  vtype: string;
  year_select: {
    name: string;
    option: string;
  };
};

const initialState: CheckoutInfoState = {
  customisePlateOptions: undefined,
  basic: null,
  financeOptions: {},
  financePrice: 0,
  maximumDeposit: 0,
  creditInformation: {
    apr: 0,
    loan_cost: "0.00",
    loan_repayment: "0.00",
    payment_regular: "0.00",
    payment_regular_detail: "0.00",
    annual_rate: "0.00",
  },
  fetchingDekoOptionStatus: false,
  fetchingBasicInfoStatus: false,
  submitFinanceStatus: false,
  deliveryPrices: undefined,
  formValues: {},
  salesFormMessage: "",
  sourcecode: "",
};

export const fetchBasicInfo = createAsyncThunk(
  "checkoutInfo/fetchBasicInfo",
  async (
    params: {
      sel: string;
      registration: string;
    },
    { dispatch }
  ) => {
    dispatch(startLoading());
    const response = await checkoutInfoClient.fetchInfo({
      sel: params.sel,
      registration: params.registration,
    });
    dispatch(endLoading());
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

export const fetchFinanceOptions = createAsyncThunk(
  "checkoutInfo/fetchFinanceOptions",
  async (
    params: {
      registration: string;
    },
    { dispatch }
  ) => {
    if (params.registration) {
      const response = await checkoutInfoClient.fetchFinanceOptions({
        registration: params.registration,
      });
      // The value we return becomes the `fulfilled` action payload
      return response;
    }
  }
);

export const getCustomiseOptions = createAsyncThunk(
  "checkoutInfo/getCustomiseOptions",
  async () => {
    const response = await checkoutInfoClient.getCustomiseOptions();
    return response;
  }
);

export const submitFinanceOption = createAsyncThunk(
  "checkoutInfo/submitFinanceOption",
  async (
    params: {
      amount: number;
      product: string;
      deposit: number;
      calculationResult?: {
        apr: string;
        annual_rate: string;
        payment_regular: string;
        payment_regular_detail: string;
        loan_repayment: string;
        loan_cost: string;
      };
    },
    { dispatch }
  ) => {
    dispatch(startLoading());
    
    // if calculationResult is provided, use it
    if (params.calculationResult) {
      dispatch(endLoading());
      return params.calculationResult;
    }

    // otherwise call API
    const response = await commonClient.financeOption({
      amount: params.amount,
      product: params.product,
      deposit: params.deposit,
    });
    dispatch(endLoading());
    return response;
  }
);

export const payAction = createAsyncThunk(
  "checkoutInfo/payAction",
  async (
    params: {
      registration: string;
      finance?: boolean;
      continueCallback?: Function;
    },
    { dispatch }
  ) => {
    dispatch(startLoading());

    if (params.finance) {
      const response = await checkoutInfoClient.payByFinance({
        registration: params.registration,
        finance: params.finance,
      });
    } else {
      const response = await checkoutInfoClient.payByCard({
        registration: params.registration,
      });
    }

    dispatch(endLoading());

    // redirect to second page
    params?.continueCallback && params?.continueCallback();
  }
);

export const getDeliveryPrices = createAsyncThunk(
  "checkoutInfo/getDeliveryPrices",
  async () => {
    const response = await checkoutInfoClient.getDeliveryPrices();
    return response;
  }
);

export const getRegistrationDetails = createAsyncThunk(
  "checkoutInfo/getRegistrationDetails",
  async (params: { registration: string }) => {
    const response = await checkoutInfoClient.getRegistrationDetails({
      registration: params.registration,
    });
    return response;
  }
);

export const getSalesFormMessage = createAsyncThunk(
  "checkoutInfo/getSalesFormMessage",
  async (params: { registration: string }) => {
    const response = await checkoutInfoClient.getSalesFormMessage({
      registration: params.registration,
    });
    return response;
  }
);

export const storeCustomiseValues = createAsyncThunk(
  "checkoutInfo/storeCustomiseValues",
  async ({ data, continueCallback }: { [key: string]: any }) => {
    const response = await checkoutInfoClient.storeCustomiseValues(data);
    continueCallback && continueCallback();
    return { response, formData: data };
  }
);

export const recoverFormValues = createAsyncThunk(
  "checkoutInfo/recoverFormValues",
  async ({ id }: { id: string }) => {
    const response = await checkoutInfoClient.getOrderValues({ id });

    return response;
  }
);

export const submitEnquiry = createAsyncThunk(
  "checkoutInfo/submitEnquiry",
  async ({
    data,
    continueCallback,
    failedCallback,
  }: {
    [key: string]: any;
  }) => {
    try {
      const response = await checkoutInfoClient.submitEnquiry(data);
      continueCallback && continueCallback();
      return response;
    } catch (e) {
      failedCallback && failedCallback();
    }
  }
);

export const checkoutInfoSlice = createSlice({
  name: "checkoutInfo",
  initialState,
  reducers: {
    initializeFormId(state) {
      const registration = sessionStorage?.getItem("formRegistration") || "";
      const formId = sessionStorage?.getItem("formId") || "";

      const pathName = window.location.pathname;
      let urlRegistration = pathName.split("/").slice(-1)[0];
      urlRegistration = decodeURI(urlRegistration)
        .replace("+", "")
        .replace(" ", "");

      const sessionInfo =
        urlRegistration === registration
          ? {
            formId,
            registration,
          }
          : { formId: "", registration: "" };

      state.formValues = {
        ...state.formValues,
        ...sessionInfo,
      };
    },
  },
  extraReducers: (builder) => {
    // fetch basic info
    builder.addCase(fetchBasicInfo.fulfilled, (state, action) => {
      state.basic = action.payload as any;
      state.fetchingBasicInfoStatus = false;
    });
    builder.addCase(fetchBasicInfo.pending, (state) => {
      state.fetchingBasicInfoStatus = true;
    });
    builder.addCase(fetchBasicInfo.rejected, (state) => {
      state.fetchingBasicInfoStatus = true;
    });
    // fetch deko options
    builder.addCase(getCustomiseOptions.fulfilled, (state, action) => {
      state.customisePlateOptions = (action.payload as any).data;
    });
    builder.addCase(fetchFinanceOptions.fulfilled, (state, action) => {
      state.financeOptions = (action.payload as any).financeOptions;
      state.financePrice = (action.payload as any).financePrice;
      state.maximumDeposit = (action.payload as any).maximumDeposit;
      state.sourcecode = (action.payload as any).registrationDetails.sourcecode;
      state.fetchingDekoOptionStatus = false;
    });
    builder.addCase(fetchFinanceOptions.pending, (state) => {
      state.fetchingDekoOptionStatus = true;
    });
    builder.addCase(fetchFinanceOptions.rejected, (state) => {
      state.fetchingDekoOptionStatus = true;
    });
    // submit finance options
    builder.addCase(submitFinanceOption.fulfilled, (state, action) => {
      state.creditInformation.apr = (action.payload as any)?.apr;
      state.creditInformation.loan_cost = (action.payload as any).loan_cost;
      state.creditInformation.loan_repayment = (
        action.payload as any
      ).loan_repayment;
      state.creditInformation.payment_regular = (
        action.payload as any
      ).payment_regular;
      state.creditInformation.payment_regular_detail = (
        action.payload as any
      ).payment_regular_detail;
      state.creditInformation.annual_rate = (
        action.payload as any
      ).annual_rate;
      state.submitFinanceStatus = false;
    });
    builder.addCase(submitFinanceOption.pending, (state) => {
      state.submitFinanceStatus = true;
    });
    builder.addCase(submitFinanceOption.rejected, (state, action) => {
      state.submitFinanceStatus = true;
      if (state.basic) {
        state.basic.registrationDetails = (
          action.payload as any
        ).registrationDetails;
      } else {
        state.basic = {
          registrationDetails: (action.payload as any).registrationDetails,
          pricingType: "",
          financePrice: 0,
        };
      }
    });
    builder.addCase(getRegistrationDetails.fulfilled, (state, action) => {
      if (state.basic) {
        state.basic.registrationDetails = (action.payload as any).data;
      } else {
        state.basic = {
          registrationDetails: (action.payload as any).data,
          pricingType: "",
          financePrice: 0,
        };
      }
    });
    builder.addCase(getSalesFormMessage.fulfilled, (state, action) => {
      const { message } = (action.payload as any).data;
      state.salesFormMessage = message;
    });
    builder.addCase(getDeliveryPrices.fulfilled, (state, action) => {
      state.deliveryPrices = (action.payload as any).data;
    });

    builder.addCase(storeCustomiseValues.fulfilled, (state, action) => {
      const { response, formData } = action.payload as any;
      const data = response.data;
      const { formId } = data;
      sessionStorage?.setItem("formId", formId);
      sessionStorage?.setItem(
        "formRegistration",
        formData.registration?.replace("+", "").replace(" ", "")
      );
      state.formValues = {
        ...formData,
        formId,
      };
    });

    builder.addCase(recoverFormValues.fulfilled, (state, action) => {
      const response = action.payload as any;
      state.formValues = {
        ...state.formValues,
        ...response?.data?.FORMVALUES,
      };
    });
  },
});

// export const { fetchBasicInfo } = checkoutInfoSlice.actions;

export const selectBasicInfo = (state: RootState) => state.checkoutInfo.basic;
export const selectFinanceOptions = (state: RootState) =>
  state.checkoutInfo.financeOptions;
export const selectFinancePrice = (state: RootState) =>
  state.checkoutInfo.financePrice;
export const selectMaximumDeposit = (state: RootState) =>
  state.checkoutInfo.maximumDeposit;
export const selectCreditInformation = (state: RootState) =>
  state.checkoutInfo.creditInformation;

export const selectBasicInfoStatus = (state: RootState) =>
  state.checkoutInfo.fetchingBasicInfoStatus;
export const selectDekoOptionStatus = (state: RootState) =>
  state.checkoutInfo.fetchingDekoOptionStatus;
export const selectCreditInfoStatus = (state: RootState) =>
  state.checkoutInfo.submitFinanceStatus;
export const selectSourcecode = (state: RootState) =>
  state.checkoutInfo.sourcecode;

export const selectCustomisePlateOptions = (state: RootState) =>
  state.checkoutInfo.customisePlateOptions;

export const selectRegistrationDetails = (state: RootState) => {
  return state.checkoutInfo.basic?.registrationDetails;
};

export const selectVehicleOptions = (state: RootState) => {
  return state.checkoutInfo.basic?.registrationDetails?.year_select?.option;
};

export const selectDeliveryPrices = (state: RootState) => {
  return state.checkoutInfo.deliveryPrices;
};

export const selectFormValues = (state: RootState) => {
  return state.checkoutInfo.formValues;
};

export const selectFaqs = (state: RootState) => state.checkoutInfo.faqs;

export const selectSalesFormMessage = (state: RootState) =>
  state.checkoutInfo.salesFormMessage;

export const { initializeFormId } = checkoutInfoSlice.actions;

export default checkoutInfoSlice.reducer;
